import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Popconfirm, message, Typography, Space, Descriptions, Modal, Input } from 'antd';
import { DeleteOutlined, EyeOutlined, SearchOutlined } from '@ant-design/icons';
import { useGetLeadsMutation, useDeleteLeadMutation } from '../redux/services/apiSlice';

const { Title } = Typography;

const Leads = () => {
    const [getLeads, { data, isLoading }] = useGetLeadsMutation();
    const [deleteLead] = useDeleteLeadMutation();

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [searchText, setSearchText] = useState('');
    const [sortOrder, setSortOrder] = useState('-createdAt');

    const fetchLeads = useCallback((page = 1, limit = 10, search = '', sort = '-createdAt') => {
        getLeads({ page, limit, search, sort });
    }, [getLeads]);

    useEffect(() => {
        fetchLeads(pagination.current, pagination.pageSize, searchText, sortOrder);
    }, [fetchLeads, pagination.current, pagination.pageSize, searchText, sortOrder]);

    const handleTableChange = (newPagination, filters, sorter) => {
        setPagination(newPagination);
        if (sorter.field) {
            const order = sorter.order === 'ascend' ? sorter.field : `-${sorter.field}`;
            setSortOrder(order);
        }
    };

    const handleSearch = (value) => {
        setSearchText(value);
        setPagination({ ...pagination, current: 1 });
    };

    const handleDelete = async (id) => {
        try {
            await deleteLead(id).unwrap();
            message.success('Lead deleted');
            fetchLeads(pagination.current, pagination.pageSize, searchText, sortOrder);
        } catch (error) {
            message.error('Error deleting');
        }
    };

    const showDetails = (record) => {
        Modal.info({
            title: 'Lead Details',
            width: 600,
            content: (
                <Descriptions bordered column={1}>
                    <Descriptions.Item label="Name">{record.name}</Descriptions.Item>
                    <Descriptions.Item label="Email">{record.email}</Descriptions.Item>
                    <Descriptions.Item label="Mobile">{record.mobileNo}</Descriptions.Item>
                    <Descriptions.Item label="Country">{record.country}</Descriptions.Item>
                    <Descriptions.Item label="State">{record.state}</Descriptions.Item>
                    <Descriptions.Item label="City">{record.city}</Descriptions.Item>
                    <Descriptions.Item label="Budget">{record.budget}</Descriptions.Item>
                    <Descriptions.Item label="Message">{record.message}</Descriptions.Item>
                </Descriptions>
            ),
        });
    };

    const columns = [
        { title: 'Date', dataIndex: 'createdAt', key: 'createdAt', render: (date) => new Date(date).toLocaleDateString(), sorter: true },
        { title: 'Name', dataIndex: 'name', key: 'name', sorter: true },
        { title: 'City', dataIndex: 'city', key: 'city', sorter: true },
        { title: 'Budget', dataIndex: 'budget', key: 'budget', sorter: true },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button icon={<EyeOutlined />} onClick={() => showDetails(record)} size="small" />
                    <Popconfirm title="Are you sure?" onConfirm={() => handleDelete(record._id)}>
                        <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
                    </Popconfirm>
                </Space>
            ),
        }
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, alignItems: 'center' }}>
                <Title level={2} style={{ margin: 0 }}>Leads Management</Title>
                <Input.Search
                    placeholder="Search leads..."
                    onSearch={handleSearch}
                    style={{ width: 300 }}
                    enterButton={<SearchOutlined />}
                />
            </div>
            <Table 
                dataSource={data?.leads || []} 
                columns={columns} 
                rowKey="_id" 
                loading={isLoading}
                pagination={{
                    current: data?.currentPage || 1,
                    pageSize: pagination.pageSize,
                    total: data?.totalItems || 0,
                    showSizeChanger: true
                }}
                onChange={handleTableChange}
            />
        </div>
    );
};

export default Leads;
